import { useState, MouseEvent, useEffect } from 'react'
import { GetQcTasksQcTasksGetData, QcTaskCategory, Task } from '@common/config/api/client'
import { usePaginatedTasks } from '@qc_task_list_view/hooks/usePaginatedTasks'
import { Order } from '@common/components/Table/table.types'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useQcTaskSearch } from '@qc_task_list_view/context/QcTaskSearchContext'
import { ExtendedTask } from '@qc_task_list_view/pages/QcTaskListTableCompletedTasks'

interface UseQCTasksManagementHandlersProps {
  category: QcTaskCategory
  studyId: string
  subjectId?: string
}

export const useQCTasksManagementHandlers = ({ category, studyId, subjectId }: UseQCTasksManagementHandlersProps) => {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<keyof Task | string>('created_at')
  const [page, setPage] = useState(0)
  const { searchValue } = useQcTaskSearch()

  const [paginationParams, setPaginationParams] = useState<GetQcTasksQcTasksGetData['query']>({
    limit: ROW_PER_PAGE_OPTIONS[2],
    next_cursor: null,
    previous_cursor: null,
    category,
    study_id: studyId,
    subject_id: subjectId,
    asc_order: order == 'asc',
    order_by_field: 'created_at',
    search: searchValue || undefined,
  })

  useEffect(() => {
    setPaginationParams((prev) => ({
      ...prev,
      search: searchValue || undefined,
      next_cursor: null,
      previous_cursor: null,
    }))
    setPage(0)
  }, [searchValue])

  const { data, error, isLoading, refetch } = usePaginatedTasks(
    ['QCTasksList', category, subjectId || ''],
    paginationParams,
  )

  const handleRequestSort = (_event: MouseEvent<unknown>, property: keyof ExtendedTask) => {
    const isAsc = orderBy.split('.')[0] === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')

    setPaginationParams((prev) => ({
      ...prev,
      asc_order: !(orderBy.split('.')[0] === property && order === 'asc'),
    }))

    switch (property) {
      case 'sites':
        setOrderBy('sites.site_pseudo_id')
        setPaginationParams((prev) => ({
          ...prev,
          order_by_field: 'sites.site_pseudo_id',
        }))
        break
      case 'subject_id':
        setOrderBy('subject.subject_pseudo_id')
        setPaginationParams((prev) => ({
          ...prev,
          order_by_field: 'subject.subject_pseudo_id',
        }))
        break
      case 'timepoint_requirement':
        setOrderBy('timepoint_requirement.name')
        setPaginationParams((prev) => ({
          ...prev,
          order_by_field: 'timepoint_requirement.name',
        }))
        break
      case 'assignee':
        setOrderBy('assignee.user_name')
        setPaginationParams((prev) => ({
          ...prev,
          order_by_field: 'assignee.user_name',
        }))
        break
      default:
        setOrderBy(property)
        setPaginationParams((prev) => ({
          ...prev,
          order_by_field: property as string,
        }))
    }
  }

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    if (newPage > 0) {
      setPaginationParams((prev) => ({
        ...prev,
        next_cursor: data?.data?.pagination.next_cursor,
        previous_cursor: null,
      }))
      setPage(page + 1)
    } else if (newPage < 0) {
      setPaginationParams((prev) => ({
        ...prev,
        next_cursor: null,
        previous_cursor: data?.data?.pagination.previous_cursor,
      }))
      setPage(page - 1)
    }
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      next_cursor: null,
      previous_cursor: null,
    }))
    setPage(0)
  }

  return {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
    setPaginationParams,
    refetch,
  }
}
